/**
 * Shared room/lobby protocol envelope, common to every game in the monorepo.
 *
 * Game-specific gameplay messages (move payloads, turn end, game over) live
 * per-game — in `apps/<game>/src/network/protocol.ts` on the client side and
 * `packages/game-sessions/src/<game>/protocol.ts` on the server side. The relay layer
 * forwards them verbatim without inspecting fields beyond `type`.
 *
 * Lobby envelopes are **seat-only** — peers learn their numeric `seat` and the
 * roster of `players` in the room. Each app resolves seat→color via its own
 * `SEATS: readonly Color[]` constant in `apps/<game>/src/network/protocol.ts`,
 * shared in spirit with the server-side `seats` array passed to its
 * `GameSession` subclass.
 *
 * The only generic in this package is `StateSyncMessage<TSnapshot>` — the
 * mid-game reconnect snapshot, whose shape is supplied by the consumer.
 */

/** A peer in the room, as seen on the wire. */
export interface SeatedPlayer {
  readonly seat: number;
  readonly name: string;
  /** OAuth subject id, present only for signed-in seats. */
  readonly userId?: string;
  /** Avatar image URL, present only for signed-in seats. */
  readonly avatarUrl?: string;
  /** True iff `name` was server-overridden from a verified OAuth identity. */
  readonly verified?: boolean;
}

// --- Room / Lobby ---

export interface CreateRoomMessage {
  readonly type: "CREATE_ROOM";
  readonly playerName: string;
  /** Optional per-game config. Opaque to the relay; each game narrows it. */
  readonly variant?: string;
  /**
   * Optional seat the creator wants. The server places the creator in this
   * slot when valid (0..maxSeats-1) and shifts the seat order so the join
   * order still fills every seat. Omitted → creator lands at seat 0 (current
   * behavior).
   */
  readonly preferredSeat?: number;
  /**
   * Optional seat count for games whose room size varies per match
   * (dominoes: 2/3/4). The server narrows it against the game's namespace
   * config; unknown / out-of-range values fall back to the namespace default
   * `maxSeats`. Omitted → use the default. Ignored by fixed-seat games
   * (checkers, backgammon).
   */
  readonly seatCount?: number;
  /**
   * When true, the host wants the match to use verifiable (drand-signed)
   * randomness — currently backgammon dice rolls, later domino shuffles. The
   * server stores this on room metadata and echoes it to the joiner so they
   * can see a "Host is using verifiable rolls" banner before agreeing to the
   * match. Implicit accept: staying in the room counts as agreement.
   */
  readonly verifiable?: boolean;
  /**
   * Opaque per-game config bag. The relay layer stores and forwards it
   * untouched; each game's session narrows it to its own typed fields.
   * Currently used by domino to carry `pointGoal` (100 / 150 / 250).
   */
  readonly options?: Record<string, unknown>;
}

export interface RoomCreatedMessage {
  readonly type: "ROOM_CREATED";
  readonly roomCode: string;
  readonly sessionToken: string;
}

export interface JoinRoomMessage {
  readonly type: "JOIN_ROOM";
  readonly roomCode: string;
  readonly playerName: string;
}

/**
 * Join the room identified by an opaque `externalKey`, creating it if no room
 * exists for that key yet. The key is shared out-of-band by everyone who should
 * meet at the same table — an embedded host's instance id, an invite link — and
 * is scoped per game. It is never interpreted by the server beyond equality.
 *
 * Resolved atomically server-side, so two clients sending the same key
 * simultaneously always land in one room. This is the reason the message exists
 * at all: a client-side "JOIN, then CREATE on ROOM_NOT_FOUND" fallback races
 * itself into two rooms.
 *
 * The reply reuses the existing lobby messages — `ROOM_CREATED` when this
 * sender created the room, `ROOM_JOINED` when it joined an existing one. The
 * create-time config fields mirror `CreateRoomMessage` because this message may
 * be the one that creates the room; they are ignored when it joins.
 */
export interface JoinOrCreateRoomMessage {
  readonly type: "JOIN_OR_CREATE";
  /** Opaque, non-empty room key. Scoped per game; compared verbatim. */
  readonly externalKey: string;
  readonly playerName: string;
  /** See `CreateRoomMessage.variant`. Applied only when the room is created. */
  readonly variant?: string;
  /** See `CreateRoomMessage.seatCount`. Applied only when the room is created. */
  readonly seatCount?: number;
  /** See `CreateRoomMessage.preferredSeat`. Applied only when the room is created. */
  readonly preferredSeat?: number;
  /** See `CreateRoomMessage.verifiable`. Applied only when the room is created. */
  readonly verifiable?: boolean;
  /** See `CreateRoomMessage.options`. Applied only when the room is created. */
  readonly options?: Record<string, unknown>;
}

/**
 * Server-resolved resume: the sender asks the server to figure out, in one
 * atomic step, whether this request reclaims a seat it already holds (by
 * verified identity from the WS ticket, or by the stored `sessionToken`) or
 * seats a genuinely new player. `playerName` is required because the latter
 * outcome is always possible.
 *
 * Reuses existing replies rather than inventing a new one: `RECONNECT_OK`
 * when a held seat is reclaimed, `ROOM_JOINED` when the sender seats as a
 * new player, `ERROR` / `ROOM_FULL` when no seat is available, or
 * `RECONNECT_FAIL` / `SEAT_TAKEN` when the resolved seat is bound to a
 * different account. This is what lets a room link resolve silently on
 * return instead of always routing through the Join screen — the same
 * "resolve atomically server-side" motivation as `JoinOrCreateRoomMessage`.
 */
export interface ResumeRoomMessage {
  readonly type: "RESUME_ROOM";
  readonly roomCode: string;
  /** Used only when the request ends up seating a new player. */
  readonly playerName: string;
  /** Stored per-room token, when the client still has one. */
  readonly sessionToken?: string;
}

export interface RoomJoinedMessage {
  readonly type: "ROOM_JOINED";
  readonly roomCode: string;
  readonly sessionToken: string;
  readonly seat: number;
  readonly seatCount: number;
  readonly players: readonly SeatedPlayer[];
  /** Variant declared by the host at room create. */
  readonly variant?: string;
  /** Host's verifiable-rolls preference, declared at room create. */
  readonly verifiable?: boolean;
}

export interface OpponentJoinedMessage {
  readonly type: "OPPONENT_JOINED";
  readonly opponentName: string;
  /**
   * Full identity of the joining peer, incl. verified name/avatar. Optional
   * for one release for backward compat with pre-Task-1 servers; consumers
   * fall back to synthesizing `{ seat, name: opponentName }` when absent.
   */
  readonly player?: SeatedPlayer;
}

/**
 * Host asks the server to start the match before the room has filled — the
 * "start now, fill the empty seats with bots" escape hatch. No payload: the
 * server derives everything from room state.
 *
 * **Host-only** (slot 0); any other seat gets an ERROR / INVALID_MESSAGE.
 * The server shrinks the room to its current occupancy and then starts the
 * session exactly as the fill path does, which is what lets a room end up
 * smaller than the `[2, maxSeats]` size a CREATE_ROOM can ask for.
 *
 * Opt-in per namespace: games whose `GameNamespaceConfig` leaves
 * `allowEarlyStart` unset ignore this message entirely.
 */
export interface StartGameMessage {
  readonly type: "START_GAME";
}

export interface GameStartMessage {
  readonly type: "GAME_START";
  readonly seat: number;
  readonly seatCount: number;
  readonly players: readonly SeatedPlayer[];
  /** Variant the room is playing — echoed to every peer. */
  readonly variant?: string;
  /**
   * Authoritative seat→role mapping. Index is the slot; value is the
   * game-specific role at that slot (e.g. "white"/"black" for backgammon and
   * checkers, "n"/"e"/"s"/"w" for domino). Reflects any rotation applied by
   * `applyCreatorPreference` (preferredSeat at game start) so clients don't
   * need to mirror the server's rotation locally. Optional for backwards
   * compatibility with clients that still resolve via a static SEATS array.
   */
  readonly seats?: readonly string[];
  /** True iff the match is running in verifiable mode (host opted in). */
  readonly verifiable?: boolean;
  /**
   * drand chain hash the server will derive rolls against. Pinned per-match
   * at start so clients can record it alongside history for years-later
   * verification, even if the LoE default chain rotates.
   */
  readonly drandChainHash?: string;
}

// --- Rematch ---

export interface RematchRequestMessage {
  readonly type: "REMATCH_REQUEST";
}

export interface RematchAcceptMessage {
  readonly type: "REMATCH_ACCEPT";
}

export interface RematchDeclineMessage {
  readonly type: "REMATCH_DECLINE";
}

// --- AFK ping ---

/**
 * Waiting peer asks the server to nudge their opponent. The server enforces a
 * per-seat cooldown and silently drops requests that arrive too soon; accepted
 * pings are relayed as `OpponentPingedMessage` to the other seats.
 */
export interface PingOpponentMessage {
  readonly type: "PING_OPPONENT";
}

/** Server relay: another seat pinged this client to remind them it's their move. */
export interface OpponentPingedMessage {
  readonly type: "OPPONENT_PINGED";
}

export interface RematchStartMessage {
  readonly type: "REMATCH_START";
  readonly seat: number;
  readonly seatCount: number;
  readonly players: readonly SeatedPlayer[];
  /** Variant carried into the rematch — the room never re-negotiates it. */
  readonly variant?: string;
  /**
   * Post-rotation seat→role mapping (see `GameStartMessage.seats`). This is
   * where rematch's seat rotation surfaces on the wire — the slot index a
   * client sees never changes, but the role at that slot does.
   */
  readonly seats?: readonly string[];
  /** Verifiable-rolls flag carried into the rematch — never re-negotiated. */
  readonly verifiable?: boolean;
  /** drand chain hash carried into the rematch. */
  readonly drandChainHash?: string;
}

// --- Resign / Restart-consent (in-game overlay) ---

/**
 * Server-authoritative forfeit. Bidirectional:
 *
 * - **Client → server:** `{ type: "RESIGN" }`. The resigning seat is
 *   identified by the sender's connection — no client-supplied seat field.
 * - **Server → all peers (incl. resigner):** `{ type: "RESIGN", seat, winner }`
 *   where `seat` is the resigner's slot and `winner` is the next slot in seat
 *   order. Per-game `NetworkGameController` catches this and routes through
 *   the existing game-over flow with a transient "by resignation" label —
 *   no `GameRecord` schema bump.
 */
export interface ResignMessage {
  readonly type: "RESIGN";
  /** Server-emitted only — the resigning seat. */
  readonly seat?: number;
  /** Server-emitted only — the seat that wins by forfeit (next in seat order). */
  readonly winner?: number;
}

/** Seat proposes restarting the current match in-place (seats keep colors). */
export interface RestartRequestMessage {
  readonly type: "RESTART_REQUEST";
}

/** A restart proposal is now pending; broadcast to all seats. */
export interface RestartPendingMessage {
  readonly type: "RESTART_PENDING";
  readonly requestedBy: number;
  readonly remainingAcceptors: number;
}

export interface RestartAcceptMessage {
  readonly type: "RESTART_ACCEPT";
}

export interface RestartDeclineMessage {
  readonly type: "RESTART_DECLINE";
}

/**
 * Pending restart proposal was cancelled — either by a decline, or because a
 * peer left the room while it was pending.
 */
export interface RestartCancelledMessage {
  readonly type: "RESTART_CANCELLED";
  readonly reason: "declined" | "left";
}

/**
 * Restart request rejected outright (the requester did not even reach pending
 * state). `already_proposed` — the seat used its single per-match proposal;
 * `another_pending` — a different seat's proposal is currently open.
 */
export interface RestartRejectedMessage {
  readonly type: "RESTART_REJECTED";
  readonly reason: "already_proposed" | "another_pending";
}

/**
 * Unanimous consent reached — restart the match in-place. Mirrors the
 * `GAME_START` seats wire so clients can re-derive seat→role even though the
 * mapping is unchanged across the restart (rotation is rematch-only).
 */
export interface RestartStartMessage {
  readonly type: "RESTART_START";
  readonly seat: number;
  readonly seatCount: number;
  readonly players: readonly SeatedPlayer[];
  readonly variant?: string;
  readonly seats?: readonly string[];
  /** Verifiable-rolls flag — preserved across restart. */
  readonly verifiable?: boolean;
  /** drand chain hash — preserved across restart. */
  readonly drandChainHash?: string;
}

// --- Connection / Reconnect ---

export interface OpponentDisconnectedMessage {
  readonly type: "OPPONENT_DISCONNECTED";
  readonly gracePeriodEndsAt: number;
}

export interface OpponentReconnectedMessage {
  readonly type: "OPPONENT_RECONNECTED";
}

export interface ReconnectRoomMessage {
  readonly type: "RECONNECT_ROOM";
  readonly roomCode: string;
  readonly sessionToken: string;
}

export interface ReconnectOkMessage {
  readonly type: "RECONNECT_OK";
  readonly roomCode: string;
  readonly seat: number;
  readonly seatCount: number;
  readonly players: readonly SeatedPlayer[];
  /** Variant of the room being rejoined. */
  readonly variant?: string;
  /**
   * Post-rotation seat→role mapping (see `GameStartMessage.seats`). Carries
   * any rematch / preferredSeat rotation the rejoiner missed while offline.
   */
  readonly seats?: readonly string[];
  /** Verifiable-rolls flag of the room being rejoined. */
  readonly verifiable?: boolean;
  /** drand chain hash of the room being rejoined. */
  readonly drandChainHash?: string;
}

export type ReconnectFailReason =
  | "EXPIRED"
  | "INVALID_TOKEN"
  | "ROOM_NOT_FOUND"
  | "SEAT_TAKEN";

export interface ReconnectFailMessage {
  readonly type: "RECONNECT_FAIL";
  readonly reason: ReconnectFailReason;
}

export interface RequestStateMessage {
  readonly type: "REQUEST_STATE";
}

/**
 * Mid-game state snapshot, forwarded by the survivor to the rejoiner during
 * reconnect. The snapshot shape is opaque to the relay — each game supplies
 * its own type for `TSnapshot`.
 */
export interface StateSyncMessage<TSnapshot = unknown> {
  readonly type: "STATE_SYNC";
  readonly snapshot: TSnapshot;
}

export interface ErrorMessage {
  readonly type: "ERROR";
  readonly code: ErrorCode;
  readonly message: string;
}

export interface PingMessage {
  readonly type: "PING";
}

export interface PongMessage {
  readonly type: "PONG";
}

// --- Drain ---

/**
 * Broadcast to every open socket (game namespaces and `/ws/lobby` alike)
 * when `RoomServer.drain()` starts a graceful shutdown, just before the
 * server staggers each socket's `close(1001, "draining")`. No client
 * consumer yet — `ConnectionManager` already treats a 1001 close as
 * retryable and reconnects on its own — the message exists so a later
 * milestone can distinguish "the server is restarting" from "your network
 * died" without another protocol change.
 */
export interface ServerDrainingMessage {
  readonly type: "SERVER_DRAINING";
  readonly retryAfterMs: number;
}

export type ErrorCode =
  | "ROOM_NOT_FOUND"
  | "ROOM_FULL"
  | "INVALID_MESSAGE"
  | "NOT_YOUR_TURN"
  | "ROOM_EXPIRED";

// --- Envelope unions ---

/**
 * Every lobby-level message a client may send. Game-specific move payloads
 * are appended by each app in its own combined `ClientMessage` union.
 */
export type LobbyClientMessage =
  | CreateRoomMessage
  | JoinRoomMessage
  | JoinOrCreateRoomMessage
  | StartGameMessage
  | ReconnectRoomMessage
  | ResumeRoomMessage
  | RematchRequestMessage
  | RematchAcceptMessage
  | RematchDeclineMessage
  | ResignMessage
  | RestartRequestMessage
  | RestartAcceptMessage
  | RestartDeclineMessage
  | PingOpponentMessage
  | PingMessage;

/**
 * Every lobby-level message the server may emit. Game-specific gameplay
 * messages forwarded by the relay are appended by each app in its own
 * combined `ServerMessage` union.
 */
export type LobbyServerMessage =
  | RoomCreatedMessage
  | RoomJoinedMessage
  | OpponentJoinedMessage
  | GameStartMessage
  | RematchRequestMessage
  | RematchAcceptMessage
  | RematchDeclineMessage
  | RematchStartMessage
  | OpponentPingedMessage
  | ResignMessage
  | RestartPendingMessage
  | RestartCancelledMessage
  | RestartRejectedMessage
  | RestartStartMessage
  | OpponentDisconnectedMessage
  | OpponentReconnectedMessage
  | ReconnectOkMessage
  | ReconnectFailMessage
  | RequestStateMessage
  | ErrorMessage
  | PongMessage
  | ServerDrainingMessage;

/**
 * Any non-lobby message — gameplay moves, turn end, game over, state sync.
 * The relay layer forwards these via the per-game session without inspecting
 * fields beyond `type`. Game sessions define stricter message types and may
 * narrow this envelope before relaying.
 */
export interface GameEnvelopeMessage {
  readonly type: string;
  readonly [field: string]: unknown;
}

/**
 * Application close code telling a client not to reconnect: the server
 * deliberately retired this socket (e.g. a user past the lobby-socket cap).
 * Auto-reconnecting would only evict the next-oldest socket in turn.
 * 4000–4999 is the range RFC 6455 reserves for applications.
 */
export const CLOSE_CODE_NO_RECONNECT = 4001;

export * from "./lobbySocket.js";
export * from "./roomCode.js";
